import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import MainLayout from './MainLayout'; // ✅ Import MainLayout

const JobStatusPage = () => {
  const [status, setStatus] = useState('Checking job status...');
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [visualizedData, setVisualizedData] = useState({});
  const [checkedFiles, setCheckedFiles] = useState({});

  const jobId = localStorage.getItem('job_id');
  const projectId = localStorage.getItem('project_id');
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!jobId || !projectId || !token) {
      setError('Missing job_id, project_id, or token in localStorage.');
      return;
    }

    const checkStatusAndFetchResults = async () => {
      try {
        const statusResponse = await axios.get(`https://openmkm.vidyayug.com/api/status/${jobId}`, {
          headers: { Authorization: token },
        });
        const jobStatus = statusResponse.data?.status;
        setStatus(`Job status: ${jobStatus}`);

        if (jobStatus === 'SUCCESS') {
          const resultResponse = await axios.get(`https://openmkm.vidyayug.com/api/results/${projectId}`, {
            headers: { Authorization: token },
          });
          const resultFiles = resultResponse.data?.results || [];
          setResults(resultFiles);
        } else if (jobStatus === 'failed') {
          setError('The job has failed.');
        } else {
          setTimeout(checkStatusAndFetchResults, 3000);
        }
      } catch (err) {
        setError('Error fetching job status or results.');
        console.error(err);
      }
    };

    checkStatusAndFetchResults();
  }, [jobId, projectId, token]);

  const handleCheckboxChange = async (file) => {
    const updatedChecked = { ...checkedFiles, [file]: !checkedFiles[file] };
    setCheckedFiles(updatedChecked);

    if (!checkedFiles[file]) {
      try {
        const response = await axios.get(
          `https://openmkm.vidyayug.com/api/visualize-dat/${projectId}/${file}`,
          { headers: { Authorization: token } }
        );

        const data = response.data;

        const chartData = data.time?.map((t, i) => {
          const row = { time: t };
          Object.keys(data).forEach((key) => {
            if (key !== 'time' && key !== 'image_base64') {
              row[key] = data[key][i];
            }
          });
          return row;
        }) || [];

        setVisualizedData(prev => ({
          ...prev,
          [file]: {
            chartData,
            imageBase64: data.image_base64 || null
          }
        }));
      } catch (error) {
        console.error('Error fetching visualization data:', error);
        setVisualizedData(prev => ({
          ...prev,
          [file]: { error: 'Failed to load data.' }
        }));
      }
    } else {
      const newData = { ...visualizedData };
      delete newData[file];
      setVisualizedData(newData);
    }
  };

  return (
    <MainLayout>
      <div style={styles.container}>
        <div style={styles.card}>
          <h2>Job Status</h2>
          {error && <p style={{ color: 'red' }}>{error}</p>}
          {!error && <p>{status}</p>}

          {results.length > 0 && (
            <>
              <h3>Result Files:</h3>
              <ul style={styles.resultList}>
                {results.map((file, index) => (
                  <li key={index} style={styles.resultItem}>
                    <label>
                      <input
                        type="checkbox"
                        checked={!!checkedFiles[file]}
                        onChange={() => handleCheckboxChange(file)}
                      />
                      <span style={{ marginLeft: '8px' }}>{file}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </>
          )}

          {Object.keys(visualizedData).length > 0 && (
            <>
              <h3>Visualization:</h3>
              {Object.entries(visualizedData).map(([file, data]) => (
                <div key={file} style={{ marginBottom: '40px' }}>
                  <h4>{file}</h4>

                  {Array.isArray(data.chartData) && data.chartData.length > 0 && (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={data.chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="time" label={{ value: 't(s)', position: 'insideBottomRight', offset: -5 }} />
                        <YAxis label={{ value: 'Values', angle: -90, position: 'insideLeft' }} />
                        <Tooltip />
                        <Legend />
                        {Object.keys(data.chartData[0])
                          .filter((key) => key !== 'time')
                          .map((key, i) => (
                            <Line
                              key={key}
                              type="monotone"
                              dataKey={key}
                              stroke={colors[i % colors.length]}
                              dot={false}
                            />
                          ))}
                      </LineChart>
                    </ResponsiveContainer>
                  )}

                  {data.imageBase64 && (
                    <div style={{ marginTop: '20px' }}>
                      <h5>Image Visualization</h5>
                      <img
                        src={`data:image/png;base64,${data.imageBase64}`}
                        alt={`Visualization for ${file}`}
                        style={{
                          width: '100%',
                          maxHeight: '400px',
                          objectFit: 'contain',
                          border: '1px solid #ccc',
                          borderRadius: '4px'
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

const colors = ['#8884d8', '#82ca9d', '#ff7300', '#ff0000', '#0088FE', '#00C49F'];

const styles = {
  container: {
    background: '#f9fafb',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: '40px',
    minHeight: 'calc(100vh - 40px)',
  },
  card: {
    background: '#fff',
    padding: '30px',
    borderRadius: '8px',
    width: '850px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    fontFamily: 'Arial, sans-serif',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  resultList: {
    listStyleType: 'none',
    padding: 0,
  },
  resultItem: {
    marginBottom: '10px',
  },
};

export default JobStatusPage;
